import { useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Grid,
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Button,
  Stack,
  TextField,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ButtonGroup,
  CardActionArea,
  IconButton,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import useEmployeesStore from "../lib/employeesStore";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import EmployeeCard from "../components/employeesCard";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const Employees = () => {
  const theme = useTheme();
  const {
    employees,
    loading,
    name,
    password,
    position,
    fetchEmployees,
    handleAddEmployee,
    handleDelete,
    handleEditRole,
    handleUpdateStatus,
    setFile,
    setName,
    setPassword,
    setPosition,
    uploadProfilePicture,
  } = useEmployeesStore();

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleFileChange = async (e, user) => {
    const file = e.target.files[0];
    if (file) {
      await uploadProfilePicture(file, user.id);
    }
  };

  return (
    <>
      <Box className="bg-white p-4 shadow-md" sx={{ borderRadius: 1 }}>
        <Stack direction="row" spacing={2} sx={{ flexWrap: "nowrap" }}>
          <TextField
            size="large"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            label="Name"
            variant="outlined"
          />
          <TextField
            size="large"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            label="Password"
            variant="outlined"
          />
          <FormControl size="large" fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={position}
              onChange={(e) => setPosition(e.target.value)}
            >
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="waiter">Waiter</MenuItem>
            </Select>
          </FormControl>
          <Button
            fullWidth
            component="label"
            role={undefined}
            variant="outlined"
            tabIndex={-1}
            startIcon={<CloudUploadIcon />}
          >
            Upload Profile Image
            <VisuallyHiddenInput
              type="file"
              onChange={(event) => setFile(event.target.files[0])}
              multiple
            />
          </Button>
          <Button
            variant="contained"
            sx={{ whiteSpace: "nowrap" }}
            onClick={handleAddEmployee}
            fullWidth
          >
            Add New Employee
          </Button>
        </Stack>
      </Box>

      <Box
        className="my-2 bg-white p-4 shadow-md"
        sx={{
          borderRadius: 1,
          // height: loading ? 200 : 800, // Smaller height for loader, larger for table
          // display: 'flex',
          // justifyContent: 'center',
          // alignItems: 'center',
        }}
      >
        {loading ? (
          <LinearProgress />
        ) : (
          <>
            <Grid container spacing={4}>
              {employees.map((employee, i) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      boxShadow: 3,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    {/* === Profile Picture Upload Section === */}
                    <CardActionArea
                      component="label"
                      sx={{ position: "relative" }}
                    >
                      <CardMedia
                        component="img"
                        alt={employee.name}
                        height="180"
                        image={employee.image || "/placeholder.jpg"}
                        sx={{ objectFit: "cover", transition: "0.3s" }}
                      />

                      {/* Hidden File Input */}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, employee)}
                      />

                      {/* Camera Icon Overlay */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "rgba(0,0,0,0.6)",
                          borderRadius: "50%",
                          p: 0.7,
                        }}
                      >
                        <IconButton
                          size="small"
                          sx={{ color: "white" }}
                          component="span" // ensures click triggers file input
                        >
                          <CameraAltIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Optional overlay hint when hovering */}
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          width: "100%",
                          background: "rgba(0, 0, 0, 0.4)",
                          color: "white",
                          textAlign: "center",
                          py: 0.5,
                          fontSize: 12,
                          opacity: 0,
                          transition: "opacity 0.3s",
                          "&:hover": { opacity: 1 },
                        }}
                      >
                        Change Profile Picture
                      </Box>
                    </CardActionArea>

                    {/* === Employee Info === */}
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6" fontWeight={600}>
                          {employee.name}
                        </Typography>
                        <Chip
                          label={
                            employee.status === "active" ? "Active" : "Inactive"
                          }
                          color={
                            employee.status === "active" ? "success" : "error"
                          }
                          size="medium"
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={500}
                        sx={{ textTransform: "uppercase" }}
                      >
                        {employee.role}
                      </Typography>
                    </CardContent>

                    {/* === Action Buttons === */}
                    <ButtonGroup variant="contained" fullWidth>
                      <Button
                        startIcon={<EditIcon />}
                        onClick={() => handleEditRole(employee)}
                        color="primary"
                      >
                        Edit Role
                      </Button>
                      <Button
                        startIcon={
                          employee.status === "active" ? (
                            <PersonOffIcon />
                          ) : (
                            <PersonAddIcon />
                          )
                        }
                        onClick={() => handleUpdateStatus(employee)}
                        color={
                          employee.status === "active" ? "warning" : "success"
                        }
                      >
                        {employee.status === "active"
                          ? "Deactivate"
                          : "Activate"}
                      </Button>
                      <Button
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(employee)}
                        color="error"
                      >
                        Delete
                      </Button>
                    </ButtonGroup>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </>
  );
};

export default Employees;
